/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Storytelling
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'gemini/features/dashboard/dashboardPrint/api/AssetPrintAPI',
	'gemini/lib/@waca/dashboard-common/dist/core/APIFactory'
], function ($, AssetPrintAPI, APIFactory) {
	class StoryPrint {

		constructor() {
			this.style = {};
			this.containerClass = '.tabCntr';
		}

		/**
		 * Creates the api to be used by the Print feature
		 * @returns {Object} returns the api
		 */
		getAPI() {
			return APIFactory.createAPI(this, [AssetPrintAPI]);
		}

		/**
		 * Shows all the scenes and appends/removes styles as necessary for printing
		 * @param content.context the printing context
		 * @param {array} content.modelIds id's of the different page contents of the asset
		 * @param {string} content.layoutType  type of the layout
		 * @param {object} content.printPageSize the size of the pdf paper that the user chose
		 * @param {object} content.pageElement node referring to the root page element of the current view
		 * @returns {Promise} returns the promise of moving all the scenes to the end state
		 */
		showContentsForPrint(content) {
			this.storyAPI = content.context.getCurrentContentView().getStoryAPI();
			this.initCursorTime = this.storyAPI.getCurrentTime();
			const $pageElement = $(content.pageElement);
			this._addBlocker($pageElement);
			return this.storyAPI.enterPrintMode().then(() => {
				const modelIds = content.modelIds;

				this.$timelinePlayer = $pageElement.find('.timelinePlayer');

				let $holdDiv = $pageElement.find(this.containerClass);
				$holdDiv.css('display', 'block');
				this.layoutType = content.layoutType;
				this.isSlideShow = this._getStoryType(content.layoutType) === 'slideshow';
				const pageSize = content.printPageSize;
				if (this.isSlideShow) {
					this._showContentsForSlideShow(modelIds, $pageElement, $holdDiv, pageSize);
				} else {
					this._showContentsForGuidedJourney(modelIds, $pageElement, $holdDiv, pageSize);
				}
				this.$timelinePlayer.hide();
			});
		}

		/**
		 * Hides the previously shows scenes and adds/removes the styles after printing
		 * @param {array} modelIds id's of the different page contents of the asset
		 * @param {object} pageElement node referring to the root page element of the current view
		 * @returns {Promise}
		 */
		hideContentsAfterPrint(modelIds, pageElement) {
			const $pageElement = $(pageElement);
			$pageElement.find(this.containerClass).css('display', '');
			$pageElement.find('.page').first().css('display', 'flex');

			return this.storyAPI.leavePrintMode(this.initCursorTime).then(() => {
				modelIds.forEach(modelId => {
					const $page = $pageElement.find('#' + modelId + '_tab');
					const style = this.style[modelId];
					if (this.isSlideShow) {
						if (!$page.hasClass('selected')) {
							$page.toggleClass('hiddenScene', true);
						}
						$page[0].style.width = style.width;
						$page[0].style.height = style.height;
						$page.find('.page.pagecontainer')[0].style.width = style.childWidth;
						$page.find('.page.pagecontainer')[0].style.height = style.childHeight;
					} else {
						$page.css('transform-style', style.transformStyle);
						$page.toggleClass('step', true);
						$page.toggleClass('hiddenScene', false);
						$page.find('.sceneInfo').show();
						if( this.layoutType === 'panAndZoom6' ){
							$page.find('.sceneConnector').show();
							$page.find('.sequenceTimeline').show();
							$page.find('.sequenceSceneTitle').show();
						}
						this.$startOverview.after($page);
						this.$startOverview = $page;

						$page.css('width', style.width);
						$page.css('height', style.height);
						$page.find('.page.pagecontainer').css('width', style.childWidth);
						$page.find('.page.pagecontainer').css('height', style.childHeight);
					}
					$page.css('transform', style.transform);
				});
				this.$timelinePlayer.show();
			}).then(this._removeBlocker.bind(this));
		}

		/**
		 * Dummy function
		 * @returns {boolean} returns false as offscreen page size not needed for stories
		 */
		getOffScreenPageSize() {
			return false;
		}

		/**
		 * Determines if the story is slideshow or guided journey
		 * @param {stirng} layoutType an array of model objects
		 * @returns {boolean} returns true if the story is of type slideshow else false referring to guided journey
		 */
		_getStoryType(layoutType) {
			return layoutType === 'slideshow' ? 'slideshow' : 'panAndZoom';
		}

		/**
		 * Shows all the scenes and appends/removes styles for slideshow stories as necessary for printing
		 * @param {array} modelIds id's of the different page contents of the asset
		 * @param {object} $pageElement node referring to the root page element of the current view
		 * @param {object} $holdDiv node referring to the parent element containing all the nodes for the scenes
		 */
		_showContentsForSlideShow(modelIds, $pageElement, $holdDiv, pageSize) {
			this.originalParentMap = {};
			const conversionFactor = 0.75;
			const $selectedScene = $pageElement.find('.pageTabContent.selected');
			const scaleFactor = $selectedScene.width() / $selectedScene.height();

			//Needed for firefox as display: flex produces inconsistent behaviour with page-breaks
			$pageElement.find('.page').first().css('display', 'block');

			modelIds.forEach(modelId => {
				const $page = $pageElement.find('#' + modelId + '_tab');
				const $pageContainer = $page.find('.page.pagecontainer');
				this.style[modelId] = {
					width: $page[0].style.width,
					height: $page[0].style.height,
					childWidth: $pageContainer[0].style.width,
					childHeight: $pageContainer[0].style.height,
					transform: $page[0].style.transform
				};
				if (!$page.hasClass('selected')) {
					$page.css('transform', '');
					$page.toggleClass('hiddenScene', false);
				}
				// Printing page size width and height in px
				const pdfSizeWidth = pageSize.width / conversionFactor;
				const pdfSizeHeight = pageSize.height / conversionFactor;

				const scaledWidth = pdfSizeWidth*1.5;
				const scaledHeight = scaledWidth/scaleFactor;

				$page[0].style.width = scaledWidth + 'px';
				$page[0].style.height = Math.min(scaledHeight, pdfSizeHeight) + 'px';

				$pageContainer[0].style.width = scaledWidth + 'px';
				$pageContainer[0].style.height = Math.min(scaledHeight, pdfSizeHeight) + 'px';

				this.originalParentMap[modelId] = $page.parent();
				$holdDiv.append($page);
			});
		}

		/**
		 * Shows all the scenes and appends/removes styles for guided journey stories as necessary for printing
		 * @param {array} modelIds id's of the different page contents of the asset
		 * @param {object} $pageElement node referring to the root page element of the current view
		 * @param {object} $holdDiv node referring to the parent element containing all the nodes for the scenes
		 * @param {object} content current content of the story
		 */
		_showContentsForGuidedJourney(modelIds, $pageElement, $holdDiv, pageSize) {
			this.$startOverview = $pageElement.find('[id^="start_overview_"]');
			const converstionFactor = 0.75;
			// Printing page size width and height in px
			const pdfSizeWidth = pageSize.width / converstionFactor;
			const pdfSizeHeight = pageSize.height / converstionFactor;

			//Needed for firefox as display: flex produces inconsistent behaviour with page-breaks
			$pageElement.find('.page').first().css('display', 'block');

			modelIds.forEach(modelId => {
				const $page = $pageElement.find('#' + modelId + '_tab');
				const $pageContainer = $page.find('.page.pagecontainer');
				const scaleFactor = $pageContainer.width()/$pageContainer.height();
				this.style[modelId] = {
					width: $page.width(),
					height: $page.height(),
					childWidth: $pageContainer.width(),
					childHeight: $pageContainer.height(),
					transformStyle: $page.css('transform-style'),
					transform: $page.css('transform')
				};
				const scaledWidth = pdfSizeWidth*1.5;
				const scaledHeight = scaledWidth/scaleFactor;	//making sure the height is proportionate to the width
				$page.width(scaledWidth);
				$page.height(Math.min(scaledHeight, pdfSizeHeight));

				$pageContainer.width(scaledWidth);
				$pageContainer.height(Math.min(scaledHeight, pdfSizeHeight));

				$page.find('.sceneInfo').hide();
				if(this.layoutType === 'panAndZoom6'){
					$page.find('.sceneConnector').hide();
					$page.find('.sequenceTimeline').hide();
					$page.find('.sequenceSceneTitle').hide();
				}
				$page.css('transform-style', '');
				$page.removeClass('step');
				$page.css('transform', '');

				$holdDiv.append($page);
			});
		}

		/**
		 * Adds a blocker while building the pdf to prevent user from seeing the works in the background
		 * @param {object} $pageElement node referring to the root page element of the current view
		 */
		_addBlocker($pageElement) {
			this.blocker = $('<div>', {
				'class': 'printBlocker'
			});
			$pageElement.append(this.blocker);
		}

		/**
		 * Removes the blocker after printing to pdf is completed
		 */
		_removeBlocker() {
			this.blocker.remove();
			this.blocker = null;
		}
	}

	return StoryPrint;
});
